/* predictive-search.js — standalone behavior for snippets/predictive-search.liquid.
   Namespace: window.themePredictiveSearch (kept separate from assets/theme.js's
   `window.theme`, which this task must not touch -- see task-7-report.md).

   Self-initializes by scanning for every `[data-predictive-search]` root in
   the DOM, so it works no matter where/when the snippet gets mounted (the
   controller wires the snippet into sections/header.liquid separately from
   this file).

   Fetches Shopify's native predictive search JSON endpoint:
     /search/suggest.json?q=<query>&resources[type]=product&resources[limit]=6
       &resources[options][unavailable_products]=last
   (https://shopify.dev/docs/api/ajax/reference/predictive-search) rather
   than the section-rendering variant (`&section_id=...`, which asks Shopify
   to return pre-rendered HTML from a *section* template) -- the task brief
   asked for "the JSON API", and this keeps the widget fully self-contained
   with no extra section file to render suggestion markup.

   Per that same doc, `product.price`/`price_min`/`price_max` come back as
   plain decimal strings ("19.99"), NOT pre-formatted with the shop's money
   format -- so `formatMoney()` below re-implements Shopify's classic
   `money_format` token syntax (the same `{{amount}}`-style tokens the
   `money` Liquid filter uses) against `data-money-format` (the raw
   `shop.money_format` value, passed down from the Liquid snippet since this
   file has no access to Liquid objects).

   Explicit states only: loading, error (network/HTTP failure -- never
   swallowed), empty ("no suggestions"), and results. No fake placeholder
   suggestions are ever rendered.
*/
(function () {
  'use strict';

  var DEBOUNCE_MS = 200;
  var SUGGEST_URL = '/search/suggest.json';
  var DEFAULT_MONEY_FORMAT = '${{amount}}';

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  function escapeHtml(value) {
    var map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return map[ch];
    });
  }

  // Reimplements Shopify's classic money_format token syntax client-side.
  // `amount` is a plain decimal string like "19.99" (not cents), matching
  // what /search/suggest.json actually returns for product prices.
  function formatWithDelimiters(number, precision, thousands, decimalSep) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimalSep = decimalSep == null ? '.' : decimalSep;

    if (isNaN(number) || number == null) return '0';

    number = (number / 100).toFixed(precision);

    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimalSep + parts[1] : '';

    return dollars + cents;
  }

  function formatMoney(decimalAmount, format) {
    var formatString = format || DEFAULT_MONEY_FORMAT;
    var cents = Math.round(parseFloat(decimalAmount || '0') * 100);
    var placeholderMatch = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
    var token = placeholderMatch ? placeholderMatch[1] : 'amount';
    var value;

    switch (token) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount':
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return placeholderMatch ? formatString.replace(placeholderMatch[0], value) : value;
  }

  function initWidget(root) {
    var toggle = root.querySelector('[data-predictive-search-toggle]');
    var form = root.querySelector('[data-predictive-search-form]');
    var input = root.querySelector('[data-predictive-search-input]');
    var closeBtn = root.querySelector('[data-predictive-search-close]');
    var resultsBox = root.querySelector('[data-predictive-search-results]');

    if (!form || !input || !resultsBox) return;

    var searchUrl = root.getAttribute('data-search-url') || form.getAttribute('action') || '/search';
    var moneyFormat = root.getAttribute('data-money-format') || DEFAULT_MONEY_FORMAT;
    var activeController = null;

    root.classList.add('js-enabled');

    function openPanel() {
      root.classList.add('open');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      input.focus();
    }

    function closePanel() {
      root.classList.remove('open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      closeResults();
    }

    function closeResults() {
      if (activeController) {
        activeController.abort();
        activeController = null;
      }
      resultsBox.hidden = true;
      resultsBox.innerHTML = '';
    }

    function renderLoading() {
      resultsBox.hidden = false;
      resultsBox.innerHTML = '<p class="predictive-search-status">Caricamento…</p>';
    }

    function renderError() {
      resultsBox.hidden = false;
      resultsBox.innerHTML =
        '<p class="predictive-search-status predictive-search-status--error">' +
        'Impossibile caricare i suggerimenti. Riprova.</p>';
    }

    function renderEmpty(query) {
      resultsBox.hidden = false;
      resultsBox.innerHTML =
        '<p class="predictive-search-status">Nessun suggerimento per &quot;' + escapeHtml(query) + '&quot;</p>';
    }

    function renderResults(products, query) {
      var html = '<ul class="predictive-search-list">';

      for (var i = 0; i < products.length; i++) {
        var product = products[i];
        var imageUrl = (product.featured_image && product.featured_image.url) || product.image || '';
        var price = escapeHtml(formatMoney(product.price, moneyFormat));

        html += '<li>' +
          '<a class="predictive-search-item" role="option" href="' + escapeHtml(product.url) + '">' +
          (imageUrl
            ? '<img class="predictive-search-thumb" src="' + escapeHtml(imageUrl) + '" width="48" height="48" alt="" loading="lazy">'
            : '<span class="predictive-search-thumb predictive-search-thumb--empty" aria-hidden="true"></span>') +
          '<span class="predictive-search-item-info">' +
          '<span class="predictive-search-item-title">' + escapeHtml(product.title) + '</span>' +
          '<span class="predictive-search-item-price">' + price + '</span>' +
          '</span>' +
          '</a>' +
          '</li>';
      }

      html += '</ul>';
      html += '<a class="predictive-search-view-all" href="' + searchUrl + '?q=' + encodeURIComponent(query) +
        '&type=product">Vedi tutti i risultati per &quot;' + escapeHtml(query) + '&quot;</a>';

      resultsBox.hidden = false;
      resultsBox.innerHTML = html;
      resultsBox.setAttribute('role', 'listbox');
    }

    function fetchSuggestions(query) {
      if (activeController) activeController.abort();
      activeController = 'AbortController' in window ? new AbortController() : null;

      renderLoading();

      var url = SUGGEST_URL +
        '?q=' + encodeURIComponent(query) +
        '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';

      fetch(url, { signal: activeController ? activeController.signal : undefined })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('predictive search request failed with status ' + response.status);
          }
          return response.json();
        })
        .then(function (data) {
          var products = data && data.resources && data.resources.results && data.resources.results.products;
          if (!Array.isArray(products)) {
            // HTTP 200 but the JSON doesn't have the expected
            // `resources.results.products` shape -- an unexpected/malformed
            // response, not a genuine "zero suggestions" result. Surface the
            // error state so the two aren't confused.
            renderError();
            return;
          }
          if (products.length === 0) {
            renderEmpty(query);
          } else {
            renderResults(products, query);
          }
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          renderError();
        });
    }

    var handleInput = debounce(function () {
      var query = input.value.trim();
      if (query.length === 0) {
        closeResults();
        return;
      }
      fetchSuggestions(query);
    }, DEBOUNCE_MS);

    input.addEventListener('input', handleInput);

    input.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowDown') return;
      var firstItem = resultsBox.querySelector('.predictive-search-item');
      if (firstItem) {
        event.preventDefault();
        firstItem.focus();
      }
    });

    resultsBox.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      var items = Array.prototype.slice.call(resultsBox.querySelectorAll('.predictive-search-item'));
      var currentIndex = items.indexOf(document.activeElement);
      event.preventDefault();

      if (event.key === 'ArrowDown') {
        var nextIndex = currentIndex + 1 < items.length ? currentIndex + 1 : items.length - 1;
        items[nextIndex].focus();
      } else if (currentIndex <= 0) {
        input.focus();
      } else {
        items[currentIndex - 1].focus();
      }
    });

    if (toggle) {
      toggle.addEventListener('click', function () {
        if (root.classList.contains('open')) {
          closePanel();
        } else {
          openPanel();
        }
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        input.value = '';
        closePanel();
        if (toggle) toggle.focus();
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (!resultsBox.hidden) {
        closeResults();
        input.focus();
        return;
      }
      if (root.classList.contains('open')) {
        closePanel();
        if (toggle) toggle.focus();
      }
    });

    document.addEventListener('click', function (event) {
      if (!root.classList.contains('open')) return;
      if (root.contains(event.target)) return;
      closePanel();
    });
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-predictive-search]');
    for (var i = 0; i < roots.length; i++) {
      initWidget(roots[i]);
    }
  }

  window.themePredictiveSearch = { init: initAll, formatMoney: formatMoney };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
